import * as React from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';
import axios from 'axios';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Label } from 'semantic-ui-react';
import ActorConfigCard from './ActorConfigCard';
import { useGrowSetup } from '../GrowSetupContext';

export default function ActorMainGrid(props) {
  const [allActors, setAllActors] = useState([]);
  const [isFetched, setIsFetched] = useState(false);
  const { grow_setup } = useGrowSetup();

  // Hilfsfunktionen, damit es robust gegen unterschiedliche Feldnamen ist
  const getActorId = useCallback((a) => a?.actor_id ?? a?.id ?? a?._id, []);
  const getChannel = useCallback((a) => a?.dimmer ?? a?.channel ?? null, []);
  const setChannelOn = useCallback((a, newCh) => {
    if ('dimmer' in a) return { ...a, dimmer: newCh };
    if ('channel' in a) return { ...a, channel: newCh };
    return { ...a, dimmer: newCh }; // Fallback
  }, []);

  const fetchData = useCallback(async () => {
    if (grow_setup > 0) {
      try {
        const res = await axios.post(
          `/get_actor_config/${grow_setup}`,
          { hello: 'world' },
          {
            headers: {
              'Access-Control-Allow-Origin': '*',
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + props.token,
              Method: 'GET',
            },
          }
        );
        setAllActors(res.data || []);
        setIsFetched(true);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
  }, [grow_setup, props.token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Für Debug/Transparenz: wer belegt welchen Port?
  const portOwner = useMemo(() => {
    const owners = {};
    for (const a of allActors) {
      const ch = getChannel(a);
      if (ch === 1 || ch === 2 || ch === 3) owners[ch] = a;
    }
    return owners; // {1: actorA, 2: actorB, 3: actorC}
  }, [allActors, getChannel]);

  /**
   * Callback, den die ActorConfigCard vor dem Kanalwechsel aufruft.
   * @param {string|number} actorId
   * @param {number} desiredChannel 0=Steckdose/Funk, 1..3=Port1..3
   * @returns {{ok: boolean, conflictWith?: any}}
   */
  const onTryChangeChannel = useCallback(
    (actorId, desiredChannel) => {
      // Steckdose/Funk (0) immer erlaubt
      if (desiredChannel === 0) {
        setAllActors(prev =>
          prev.map(a => (getActorId(a) === actorId ? setChannelOn(a, desiredChannel) : a))
        );
        return { ok: true };
      }

      // gewünschter Port 1..3: prüfen
      if (![1, 2, 3].includes(desiredChannel)) {
        // unbekannter Kanal → behandeln wie frei
        setAllActors(prev =>
          prev.map(a => (getActorId(a) === actorId ? setChannelOn(a, desiredChannel) : a))
        );
        return { ok: true };
      }

      // eigenen Actor + aktuellen Kanal ermitteln
      const me = allActors.find(a => getActorId(a) === actorId);
      const current = getChannel(me);

      // keine Änderung → ok
      if (current === desiredChannel) return { ok: true };

      // belegt?
      const owner = allActors.find(
        a => getChannel(a) === desiredChannel && getActorId(a) !== actorId
      );
      if (owner) {
        const ownerName = owner?.name ?? owner?.measure ?? getActorId(owner);
        window.alert(
          `Port ${desiredChannel} ist bereits dem Aktor „${ownerName}“ zugewiesen.\n` +
          `Bitte dort zuerst ändern/entfernen.`
        );
        return { ok: false, conflictWith: owner };
      }

      // frei → in den lokalen State schreiben (damit nachfolgende Prüfungen aktuell sind)
      setAllActors(prev =>
        prev.map(a => (getActorId(a) === actorId ? setChannelOn(a, desiredChannel) : a))
      );
      return { ok: true };
    },
    [allActors, getActorId, getChannel, setChannelOn]
  );



  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview
      </Typography>

      <Grid container spacing={2} columns={12} sx={{ mb: (theme) => theme.spacing(2) }}>
        {isFetched ? (
          allActors.map((actor, index) => (
            <ActorConfigCard
              key={getActorId(actor) ?? index}
              {...actor}
              token={props.token}
              grow_setup={grow_setup}
              // 👉 NEU: Callback, den die Card beim Kanalwechsel zuerst ruft
              onTryChangeChannel={onTryChangeChannel}
              // 👉 Optional: falls du direkt nach erfolgreicher Änderung speichern willst
              portOwner={portOwner}
            />
          ))
        ) : (
          <Label>Loading</Label>
        )}
      </Grid>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
